import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const supportedBundleCounts = [1, 4, 8];
const paidPairCounts = new Map([[1, 1], [4, 2], [8, 3]]);

export function buildHalloweenBundle(variants, choices, quantity) {
  const count = Number(quantity);
  if (!Number.isInteger(count) || !supportedBundleCounts.includes(count)) {
    return { error: 'Choose 1, 4 or 8 pairs.', variants: [], total: 0, items: [] };
  }
  if (!Array.isArray(choices) || choices.length !== count) {
    return { error: 'Choose a pattern and size for every pair.', variants: [], total: 0, items: [] };
  }

  const selectedVariants = [];
  for (const choice of choices) {
    const variant = Array.isArray(variants)
      ? variants.find((entry) => entry.pattern === String(choice?.pattern) && entry.size === String(choice?.size))
      : null;
    if (!variant) return { error: 'Choose a pattern and size for every pair.', variants: [], total: 0, items: [] };
    if (!variant.available) return { error: `${variant.patternName || 'This option'} is sold out in ${variant.sizeName || variant.size}.`, variants: [], total: 0, items: [] };
    if (!Number.isInteger(variant.price) || variant.price < 0 || !/^\d+$/.test(String(variant.id))) {
      return { error: 'This option is not available right now.', variants: [], total: 0, items: [] };
    }
    selectedVariants.push(variant);
  }

  const itemMap = new Map();
  selectedVariants.forEach((variant) => {
    const id = String(variant.id);
    itemMap.set(id, { id, quantity: (itemMap.get(id)?.quantity || 0) + 1 });
  });
  const subtotal = selectedVariants.reduce((sum, variant) => sum + variant.price, 0);
  const paidCount = paidPairCounts.get(count) || count;
  const total = selectedVariants
    .map((variant) => variant.price)
    .sort((first, second) => second - first)
    .slice(0, paidCount)
    .reduce((sum, price) => sum + price, 0);
  return {
    error: null,
    variants: selectedVariants,
    subtotal,
    total,
    items: Array.from(itemMap.values()),
  };
}

export function buildHalloweenSelection(variants, pattern, size, quantity) {
  const count = Number(quantity);
  return buildHalloweenBundle(variants, Array.from({ length: count }, () => ({ pattern, size })), count);
}

export class SkinEliteHalloweenProduct {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('[data-halloween-form]');
    this.variants = JSON.parse(section.querySelector('[data-halloween-variants]')?.textContent || '[]');
    this.quantity = Number(section.querySelector('[data-halloween-count-choice]:checked')?.value || 4);
    this.busy = false;
    this.cartNeedsReview = false;
    this.events = new AbortController();
    this.galleryFrame = null;
    this.stickyFrame = null;
    if (!this.form || !this.variants.length) return;
    this.bind();
    this.update(false);
  }

  listen(target, type, handler, options = {}) {
    target?.addEventListener(type, handler, { ...options, signal: this.events.signal });
  }

  bind() {
    this.section.querySelectorAll('[data-halloween-count-choice]').forEach((input) => this.listen(input, 'change', () => {
      this.quantity = Number(input.value);
      this.switchBundle();
      this.update(true);
    }));
    this.section.querySelectorAll('[data-halloween-pair-pattern], [data-halloween-pair-size]').forEach((input) => this.listen(input, 'change', () => {
      this.update(true);
      const firstPair = this.activeBundle()?.querySelector('[data-halloween-pair]');
      if (input.matches('[data-halloween-pair-pattern]') && input.closest('[data-halloween-pair]') === firstPair) {
        this.showSlide(input.value);
      }
    }));
    this.listen(this.form, 'submit', (event) => { event.preventDefault(); this.addToCart(); });
    this.section.querySelectorAll('[data-halloween-thumb]').forEach((button) => this.listen(button, 'click', () => this.showSlide(button.dataset.halloweenThumb)));
    this.listen(this.section.querySelector('[data-halloween-prev]'), 'click', () => this.stepGallery(-1));
    this.listen(this.section.querySelector('[data-halloween-next]'), 'click', () => this.stepGallery(1));
    const sizeDialog = this.section.querySelector('[data-halloween-size-dialog]');
    this.listen(this.section.querySelector('[data-halloween-size-open]'), 'click', () => {
      if (typeof sizeDialog?.showModal === 'function') sizeDialog.showModal();
      else sizeDialog?.setAttribute('open', '');
    });
    this.listen(sizeDialog, 'click', (event) => {
      if (event.target === sizeDialog) sizeDialog.close();
    });
    const gallery = this.section.querySelector('[data-halloween-gallery]');
    this.listen(gallery, 'scroll', () => {
      if (this.galleryFrame) cancelAnimationFrame(this.galleryFrame);
      this.galleryFrame = requestAnimationFrame(() => this.syncGalleryState());
    }, { passive: true });
    this.setupSticky();
  }

  getSelection(quantity = this.quantity) {
    const bundle = this.bundleFor(quantity);
    return buildHalloweenBundle(this.variants, this.readChoices(bundle), Number(quantity));
  }

  bundleFor(quantity) {
    return this.section.querySelector(`[data-halloween-bundle="${quantity}"]`);
  }

  activeBundle() {
    return this.bundleFor(this.quantity);
  }

  readChoices(bundle) {
    if (!bundle) return [];
    return Array.from(bundle.querySelectorAll('[data-halloween-pair]')).map((row) => ({
      pattern: row.querySelector('[data-halloween-pair-pattern]')?.value || '',
      size: row.querySelector('[data-halloween-pair-size]')?.value || '',
    }));
  }

  switchBundle() {
    this.section.querySelectorAll('[data-halloween-bundle]').forEach((bundle) => {
      const selected = Number(bundle.dataset.halloweenBundle) === this.quantity;
      bundle.classList.toggle('is-selected', selected);
      const panel = bundle.querySelector('[data-halloween-bundle-panel]');
      if (panel) panel.hidden = !selected;
    });
  }

  money(cents, withCurrency = false) {
    const currency = this.section.dataset.currency || 'USD';
    const locale = this.section.dataset.locale || 'en';
    const value = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    return withCurrency ? `${value} ${currency}` : value;
  }

  update(changeUrl) {
    const selection = this.getSelection();
    this.section.querySelectorAll('[data-halloween-offer-price]').forEach((node) => {
      const preview = this.getSelection(Number(node.dataset.halloweenOfferPrice));
      node.textContent = preview.error ? 'Unavailable' : this.money(preview.total);
    });
    this.section.querySelectorAll('[data-halloween-offer-compare]').forEach((node) => {
      const preview = this.getSelection(Number(node.dataset.halloweenOfferCompare));
      node.textContent = preview.error ? '' : this.money(preview.subtotal);
    });
    if (selection.error || !selection.variants.length) {
      this.setStatus(selection.error, true);
      this.setButtonState();
      return;
    }
    const firstVariant = selection.variants[0];
    this.section.querySelector('[data-halloween-variant-id]').value = firstVariant.id;
    this.section.querySelector('[data-halloween-quantity]').value = 1;
    const perPairLabel = this.quantity === 1 ? 'per pair' : 'average per pair';
    this.section.querySelector('[data-halloween-unit]').textContent = `${this.money(Math.round(selection.total / this.quantity))} ${perPairLabel}`;
    this.section.querySelectorAll('[data-halloween-total]').forEach((node) => { node.textContent = this.money(selection.total, true); });
    this.section.querySelector('[data-halloween-button-price]').textContent = this.money(selection.total, true);
    const extraPatterns = new Set(selection.variants.map((variant) => variant.pattern)).size > 1;
    const selectionName = extraPatterns ? 'Mixed patterns' : firstVariant.patternName;
    this.section.querySelector('[data-halloween-sticky-selection]').textContent = `${this.quantity} ${this.quantity === 1 ? 'Pair' : 'Pairs'} · ${selectionName} · ${firstVariant.sizeName || firstVariant.size}`;
    const stickyImage = this.section.querySelector('[data-halloween-sticky-image]');
    if (stickyImage && firstVariant.image) stickyImage.src = firstVariant.image;
    this.setStatus('');
    this.setButtonState();
    if (changeUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', firstVariant.id);
      window.history.replaceState({}, '', url);
    }
  }

  setButtonState() {
    const selection = this.getSelection();
    this.section.querySelectorAll('[data-halloween-add]').forEach((button) => {
      button.disabled = Boolean(selection.error || this.busy || this.cartNeedsReview);
      const label = button.querySelector('span') || button;
      label.textContent = this.busy ? 'Adding…' : selection.error ? 'Sold out' : 'Add to cart';
    });
  }

  setStatus(message, isError = false) {
    const status = this.section.querySelector('[data-halloween-status]');
    if (status) status.textContent = message || '';
    status?.classList.toggle('is-error', Boolean(isError));
  }

  slides() { return Array.from(this.section.querySelectorAll('[data-halloween-slide]')); }

  showSlide(key) {
    const gallery = this.section.querySelector('[data-halloween-gallery]');
    const slide = this.slides().find((item) => item.dataset.halloweenSlide === String(key));
    if (!gallery || !slide) return;
    gallery.scrollTo({ left: slide.offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  stepGallery(direction) {
    const slides = this.slides();
    if (!slides.length) return;
    const gallery = this.section.querySelector('[data-halloween-gallery]');
    const index = Math.round(gallery.scrollLeft / Math.max(gallery.clientWidth, 1));
    this.showSlide(slides[(index + direction + slides.length) % slides.length].dataset.halloweenSlide);
  }

  syncGalleryState() {
    const gallery = this.section.querySelector('[data-halloween-gallery]');
    const slides = this.slides();
    if (!gallery || !slides.length) return;
    const index = Math.max(0, Math.min(slides.length - 1, Math.round(gallery.scrollLeft / Math.max(gallery.clientWidth, 1))));
    const key = slides[index].dataset.halloweenSlide;
    this.section.querySelectorAll('[data-halloween-thumb]').forEach((button) => button.classList.toggle('is-active', button.dataset.halloweenThumb === key));
  }

  setupSticky() {
    const sticky = this.section.querySelector('[data-halloween-sticky]');
    const button = this.form.querySelector('[data-halloween-add]');
    const update = () => {
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = requestAnimationFrame(() => {
        const footer = document.querySelector('footer');
        const drawerOpen = Boolean(document.querySelector('theme-drawer[open], dialog[open]'));
        const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
        sticky.hidden = button.getBoundingClientRect().bottom >= 0 || Boolean(footerVisible) || drawerOpen;
      });
    };
    this.listen(window, 'scroll', update, { passive: true });
    this.listen(document.querySelector('.page-wrapper'), 'scroll', update, { passive: true });
    this.listen(window, 'resize', update, { passive: true });
    this.listen(document, 'theme-drawer:open', update);
    this.listen(document, 'theme-drawer:close', update);
    update();
  }

  cartRoutes() {
    const routes = typeof Theme !== 'undefined' ? Theme.routes : null;
    const root = window.Shopify?.routes?.root || '/';
    return { add: routes?.cart_add_url || `${root}cart/add.js`, cart: `${routes?.cart_url || `${root}cart`}.js`, page: routes?.cart_url || `${root}cart` };
  }

  async fetchCart() {
    const response = await fetch(this.cartRoutes().cart, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Your cart could not be refreshed.');
    return response.json();
  }

  async addToCart() {
    if (this.busy || this.cartNeedsReview) return;
    const selection = this.getSelection();
    if (selection.error) { this.setStatus(selection.error, true); return; }
    this.busy = true;
    this.setButtonState();
    this.setStatus('Adding your Halloween pair selection…');
    const sections = Array.from(document.querySelectorAll('cart-items-component')).map((component) => component.dataset.sectionId).filter((id, index, ids) => id && ids.indexOf(id) === index);
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    const detail = { source: 'skin-elite-halloween', sourceId: this.form.id, itemCount: this.quantity, productId: this.section.dataset.productId };
    const eventLines = selection.items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity }));
    this.form.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: eventLines, promise: deferred.promise }));
    let wasAdded = false;
    let rejected = false;
    try {
      const response = await fetch(this.cartRoutes().add, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: selection.items, sections: sections.join(','), sections_url: window.location.pathname }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status) {
        rejected = response.status >= 400 && response.status < 500;
        throw new Error(payload.description || payload.message || 'This selection could not be added.');
      }
      wasAdded = true;
      const cart = await this.fetchCart();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, items: cart.items, sections: payload.sections, didError: false } });
      this.setStatus(`${this.quantity} ${this.quantity === 1 ? 'pair' : 'pairs'} added to cart.`);
      if (!document.querySelector('cart-drawer-component[auto-open]')) window.location.assign(this.cartRoutes().page);
    } catch (error) {
      this.cartNeedsReview = true;
      const message = wasAdded
        ? 'Your selection was added, but the cart display could not refresh. Check your cart before adding again.'
        : rejected ? `${error.message} Check your cart before adding again.` : 'We could not confirm the cart update. Check your cart before adding again.';
      this.form.dispatchEvent(new CartErrorEvent({ error: message, code: rejected ? 'INVALID' : 'SERVICE_UNAVAILABLE', detail: { description: message } }));
      try {
        const cart = await this.fetchCart();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, items: cart.items, didError: true } });
      } catch (refreshError) { deferred.reject(refreshError); }
      this.setStatus(message, true);
      this.section.querySelector('[data-halloween-cart-check]').hidden = false;
      this.section.querySelector('[data-halloween-cart-check]').focus({ preventScroll: false });
    } finally {
      this.busy = false;
      this.setButtonState();
    }
  }

  destroy() {
    this.events.abort();
    if (this.galleryFrame) cancelAnimationFrame(this.galleryFrame);
    if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
    delete this.section.halloweenProduct;
  }
}

function initializeHalloweenProducts(scope = document) {
  const sections = scope.matches?.('[data-halloween-product]') ? [scope] : scope.querySelectorAll('[data-halloween-product]');
  sections.forEach((section) => {
    if (section.halloweenProduct) return;
    try { section.halloweenProduct = new SkinEliteHalloweenProduct(section); } catch (error) { console.warn('[SKIN ELITE CO. Halloween] Product controls could not initialize.', error); }
  });
}

if (typeof document !== 'undefined') {
  initializeHalloweenProducts();
  document.addEventListener('shopify:section:load', (event) => initializeHalloweenProducts(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelector?.('[data-halloween-product]')?.halloweenProduct?.destroy());
}
